import { isSafeExternalUrl, BlockedExternalUrlError } from '@/lib/openExternal';
import { downloadUrl } from '@/lib/downloadUrl';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { Spinner, Text } from '@/components/primitives';
import { Icons, vars } from '@/theme';
import { fetchDownloadUrl, type StreamEntry } from '@/api/stream';
import { reportClientError } from '@/api/clientErrors';
import { useFeedbackZone } from '@/components/ui/FeedbackZone';
import { useAuthStore, useStreamUiStore } from '@/store';
import { formMessages } from '@/constants';
import { row, rowClickable, rowUnread, leadCol, unreadDot, linkButton } from './notificationBell.css';

/**
 * Icon keyed by the backend-provided icon string (resolved per lifecycle status
 * by the stream registry). `pending` renders a spinner instead, so the
 * in-progress glyph here is only a fallback.
 */
const ICON_MAP: Record<string, React.ReactNode> = {
  download: <Icons.download style={{ color: vars.color.success.base }} />,
  upload: <Icons.upload style={{ color: vars.color.success.base }} />,
  error: <Icons.errorOutline style={{ color: vars.color.danger.base }} />,
  flag: <Icons.flag style={{ color: vars.color.warning.base }} />,
  visibility_off: <Icons.notificationsOff style={{ color: vars.color.neutral.subtle }} />,
  hourglass_top: <Icons.pending style={{ color: vars.color.neutral.subtle }} />,
  info: <Icons.info style={{ color: vars.color.primary.base }} />,
  // #875a (vingapi SEND_RESULT): "<course>: 18 sent, 2 could not be reached".
  send: <Icons.send style={{ color: vars.color.primary.base }} />,
};

/** The ICON_MAP key a row renders: the server's icon when mapped, else `info`. */
function resolvedIconName(iconName: string): string {
  return iconName in ICON_MAP ? iconName : 'info';
}

function formatTimestamp(isoString: string): string {
  try {
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return isoString;
  }
}

/**
 * #807 (vingapi#1093 S2b/S2c/S2d): import stats keys the server writes for rows
 * refused on a Ving support contact (`notify_import_complete` extra_stats; the
 * labels are verbatim server strings, not camelCased — see the
 * toCamelCase defensive test). Rendered as their own lines, never as a
 * generic "key: value" stat.
 */
export const IMPORT_REFUSED_HIDDEN_STAT = 'Refused (hidden support contact)';
export const IMPORT_ARCHIVE_REFUSED_STAT = 'Archive refused (support contact)';
const REFUSAL_STATS: ReadonlySet<string> = new Set([
  IMPORT_REFUSED_HIDDEN_STAT,
  IMPORT_ARCHIVE_REFUSED_STAT,
]);

function statCount(stats: Record<string, unknown> | undefined, key: string): number {
  const value = stats?.[key];
  return typeof value === 'number' && value > 0 ? value : 0;
}

/**
 * #1243 (vingapi#1789, Q-319 af): an import refused because date cells could
 * not be read. The server (vingapi src/core/imports/unreadable_cells.py) lists
 * up to 200 cells as `refused_cells` and the exact count left out as
 * `refused_cells_not_shown`; the stream API mapping camelCases those keys
 * (pinned by test_streamEntryRow_relies_on_stream_api_context_key_casing_1243).
 */
export const IMPORT_UNREADABLE_DATE_CELLS_CODE = 'import_unreadable_date_cells';

/**
 * #1243 (vingapi#1795/#1796): a whole-file refusal for column headers naming a
 * time zone the server does not know. Its `errors` list is the complete,
 * server-bounded list (with the server's own trailer line when cut).
 */
export const IMPORT_UNKNOWN_TIME_ZONE_CODE = 'import_unknown_time_zone';

/**
 * Refusal codes whose `errors` list is complete: View details shows every
 * line, never the 5-line cap + "... and N more". (The date-cell refusal is
 * listed too: it renders its structured cells, and falls back to `errors`
 * uncapped when the structure is malformed.)
 */
function refusalListsEveryError(code: unknown): boolean {
  return code === IMPORT_UNREADABLE_DATE_CELLS_CODE || code === IMPORT_UNKNOWN_TIME_ZONE_CODE;
}

interface RefusedCell {
  row: number;
  column: string;
  text: string;
  /** vingapi#1819: the finished display sentence; absent on entries written before it. */
  reason?: string;
  kind?: string;
}

interface CellRefusal {
  cells: RefusedCell[];
  notShown: number;
}

function isRefusedCell(value: unknown): value is RefusedCell {
  if (value === null || typeof value !== 'object') return false;
  const cell = value as Record<string, unknown>;
  if (cell.reason !== undefined && typeof cell.reason !== 'string') return false;
  if (cell.kind !== undefined && typeof cell.kind !== 'string') return false;
  return typeof cell.row === 'number' && typeof cell.column === 'string' && typeof cell.text === 'string';
}

/**
 * The structured cell list of a date-cell refusal, or null when the entry is
 * not one or its structure is malformed (the caller then shows every
 * `errors` line, never fewer than the server sent).
 */
function cellRefusal(context: Record<string, unknown>): CellRefusal | null {
  if (context.code !== IMPORT_UNREADABLE_DATE_CELLS_CODE) return null;
  const cells = context.refusedCells;
  const notShown = context.refusedCellsNotShown;
  if (!Array.isArray(cells) || !cells.every(isRefusedCell)) return null;
  if (typeof notShown !== 'number' || !Number.isInteger(notShown) || notShown < 0) return null;
  return { cells, notShown };
}

/**
 * vingapi unreadable_cells.py `cell_line`: the server's `reason` printed as
 * sent (never rebuilt); an entry without one is a pre-#1819 date cell.
 */
function refusedCellLine(cell: RefusedCell): string {
  const reason = cell.reason ? cell.reason : `could not read "${cell.text}"`;
  return `Row ${cell.row}, ${cell.column}: ${reason}`;
}

/** vingapi unreadable_cells.py `not_shown_line`. */
function notShownLine(count: number): string {
  return `... and ${count} more ${count === 1 ? 'cell' : 'cells'} not shown`;
}

function entryScope(entry: StreamEntry): 'organization' | 'network' | 'organization or network' {
  if (entry.networkId != null) return 'network';
  if (entry.organizationId != null) return 'organization';
  return 'organization or network';
}

/**
 * #1244 (1244-t): the testid of a `detail` row's details panel. The panel is
 * the row's next sibling, outside `stream-entry-<id>` (inside the row, the
 * row's onClick would toggle it shut); the staging scenario selects it by id.
 */
export function streamEntryDetailsTestId(entryId: number): string {
  return `stream-entry-${entryId}-details`;
}

/**
 * #1829-d (vingapi#837 2b §5): a contact import that left people unreachable
 * writes the generic stat `Unreachable: N` and `context.unreachable_url`
 * (camelCased to `unreachableUrl`), the Reachable: No list of the org.
 */
export const IMPORT_UNREACHABLE_STAT = 'Unreachable';

/**
 * The details panel's padding. The old 32 px left / 14 px right left 338 px of
 * the bell panel's 384; the five-stat import line needs more, so the details
 * start at the row's own 14 px edge (the unread dot's column) and the right
 * edge is 8 px.
 */
export const DETAILS_PADDING = '0 8px 12px 14px';

/**
 * The stats container of the details panel. Five stats (Created, Updated,
 * Unreachable + View, Skipped, Errors) must fit one line in the bell panel's
 * width, so the column gap is tight; a very large value may still wrap (never
 * clipped).
 */
export const STATS_ROW_STYLE = {
  display: 'flex',
  flexWrap: 'wrap',
  columnGap: 6,
  rowGap: 2,
} as const;

type UnreachableLink = { kind: 'none' } | { kind: 'ok'; url: string } | { kind: 'rejected'; raw: unknown };

/** Only an app-relative Contacts list path is a link; anything else renders none. */
function unreachableLink(entry: StreamEntry, stats: Record<string, unknown> | undefined): UnreachableLink {
  if (entry.status === 'failure') return { kind: 'none' };
  if (!stats || !(IMPORT_UNREACHABLE_STAT in stats)) return { kind: 'none' };
  const raw = entry.context?.unreachableUrl;
  if (raw === undefined || raw === null) return { kind: 'none' };
  if (typeof raw === 'string' && raw.startsWith('/contacts?')) return { kind: 'ok', url: raw };
  return { kind: 'rejected', raw };
}

function EntryDetail({ entry }: { entry: StreamEntry }) {
  const isStaff = useAuthStore((s) => s.isStaff());
  const navigate = useNavigate();
  const closePanel = useStreamUiStore((s) => s.closePanel);
  const { context } = entry;
  const link = unreachableLink(entry, context?.stats as Record<string, unknown> | undefined);
  const rejectedRaw = link.kind === 'rejected' ? String(link.raw) : null;
  useEffect(() => {
    if (rejectedRaw === null) return;
    const message = 'import unreachable_url is not an app-relative /contacts? path';
    reportClientError({
      source: 'context',
      name: 'stream-unreachable-url-rejected',
      error: new Error(message),
      message,
      context: { entryId: entry.id, url: rejectedRaw.slice(0, 200) },
    });
  }, [rejectedRaw, entry.id]);
  if (!context) return null;

  const stats = context.stats as Record<string, number> | undefined;
  const errors = context.errors as string[] | undefined;
  const error = context.error as string | undefined;
  const refusedHidden = statCount(stats, IMPORT_REFUSED_HIDDEN_STAT);
  const archiveRefused = statCount(stats, IMPORT_ARCHIVE_REFUSED_STAT);
  const isRefusalCode = refusalListsEveryError(context.code);
  // #1243: server-finished display lines (trailer included), rendered as sent.
  const rawNotes = context.notes;
  const notes = Array.isArray(rawNotes) ? rawNotes.filter((n): n is string => typeof n === 'string') : [];
  const refusal = cellRefusal(context);
  const genericStats = stats
    ? Object.entries(stats).filter(([key]) => !REFUSAL_STATS.has(key))
    : [];

  return (
    <div data-testid={streamEntryDetailsTestId(entry.id)} style={{ padding: DETAILS_PADDING }}>
      {stats && (
        <div data-testid="stream-entry-stats" style={{ ...STATS_ROW_STYLE, marginBottom: errors?.length ? 8 : 0 }}>
          {genericStats.map(([key, value]) => {
            const stat = (
              <Text key={key} variant="caption" color="secondary">
                {key}: <strong>{value}</strong>
              </Text>
            );
            if (key !== IMPORT_UNREACHABLE_STAT || link.kind !== 'ok') return stat;
            // The View link stays beside its stat when the row wraps.
            return (
              <span key={key} style={{ display: 'inline-flex', alignItems: 'baseline', gap: 4 }}>
                {stat}
                <button
                  type="button"
                  className={linkButton}
                  data-testid="stream-entry-unreachable-view"
                  onClick={() => {
                    closePanel();
                    void navigate(link.url);
                  }}
                >
                  View
                </button>
              </span>
            );
          })}
        </div>
      )}
      {(refusedHidden > 0 || archiveRefused > 0) && (
        <div data-testid="stream-entry-internal-refusals" style={{ marginBottom: errors?.length ? 8 : 0 }}>
          {refusedHidden > 0 && (
            <Text
              variant="caption"
              color="secondary"
              style={{ display: 'block' }}
              data-testid="stream-entry-import-refused-hidden"
            >
              {formMessages.internalContacts.importRefusedHidden(refusedHidden, entryScope(entry))}
            </Text>
          )}
          {archiveRefused > 0 && (
            <Text
              variant="caption"
              color="secondary"
              style={{ display: 'block' }}
              data-testid="stream-entry-import-archive-refused"
            >
              {formMessages.internalContacts.importArchiveRefused(archiveRefused)}
            </Text>
          )}
          {isStaff && (
            <Text
              variant="caption"
              color="secondary"
              style={{ display: 'block' }}
              data-testid="stream-entry-internal-staff-addendum"
            >
              {formMessages.internalContacts.staffAddendum}
            </Text>
          )}
        </div>
      )}
      {error && (
        <Text variant="caption" color="error">
          {error}
        </Text>
      )}
      {refusal ? (
        <div data-testid="stream-entry-refused-cells">
          {refusal.cells.map((cell, i) => (
            <Text key={`${i}:${cell.row}:${cell.column}`} variant="caption" color="error" style={{ display: 'block' }}>
              {refusedCellLine(cell)}
            </Text>
          ))}
          {refusal.notShown > 0 && (
            <Text variant="caption" color="secondary">
              {notShownLine(refusal.notShown)}
            </Text>
          )}
        </div>
      ) : isRefusalCode && errors && errors.length > 0 ? (
        // Complete-list refusal (or a malformed cell refusal): every line the server sent, no cap.
        <div>
          {errors.map((err, i) => (
            <Text key={i} variant="caption" color="error" style={{ display: 'block' }}>
              {err}
            </Text>
          ))}
        </div>
      ) : errors && errors.length > 0 && (
        <div>
          {errors.slice(0, 5).map((err, i) => (
            <Text key={i} variant="caption" color="error" style={{ display: 'block' }}>
              {err}
            </Text>
          ))}
          {errors.length > 5 && (
            <Text variant="caption" color="secondary">
              ... and {errors.length - 5} more
            </Text>
          )}
        </div>
      )}
      {notes.length > 0 && (
        <div data-testid="stream-entry-notes">
          {notes.map((note, i) => (
            <Text
              key={i}
              variant="caption"
              color="secondary"
              style={{ display: 'block', marginTop: i === 0 ? undefined : 4 }}
            >
              {note}
            </Text>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * One activity-stream row. Unread is shown with a dot + soft tint (the old 3px
 * left-border highlight is gone); a pending job shows a spinner and "In
 * progress" with no action; ready/failed rows expose Download / View details.
 *
 * Whether a `detail` row shows its details is owned by the caller (#1244): the
 * panel moves rows between its unread and Earlier groups, which remounts them,
 * and row-local state would drop the open details.
 */
export function StreamEntryRow({
  entry,
  onMarkRead,
  expanded,
  onToggleExpanded,
}: {
  entry: StreamEntry;
  onMarkRead: (id: number) => void;
  expanded: boolean;
  onToggleExpanded: () => void;
}) {
  const zone = useFeedbackZone();
  // Not useId: the row remounts when it moves between the unread and Earlier groups (#1244).
  const downloadSource = `stream-download:${entry.id}`;
  const navigate = useNavigate();
  const closePanel = useStreamUiStore((s) => s.closePanel);
  const [downloading, setDownloading] = useState(false);

  const isPending = entry.status === 'pending';
  const unread = !entry.viewed;
  // A navigate row is only clickable with a relative in-app path — an absolute
  // URL would route off-app, so treat it as non-navigable.
  const canNavigate = entry.action === 'navigate' && !!entry.actionUrl?.startsWith('/');
  const isClickable =
    !isPending && (entry.action === 'download' || entry.action === 'detail' || canNavigate);

  const handleClick = useCallback(async () => {
    if (isPending) return; // nothing to fetch or acknowledge yet

    if (!entry.viewed) onMarkRead(entry.id);

    if (entry.action === 'download' && entry.actionUrl) {
      setDownloading(true);
      try {
        const url = await fetchDownloadUrl(entry.actionUrl);
        // The signed URL points at an attachment. Opening it in a new tab
        // (window.open) downloads the file but strands the user on a blank
        // tab; a programmatic anchor click downloads in place. Keep the
        // scheme guard so a non-http(s) URL is still refused.
        if (!isSafeExternalUrl(url)) throw new BlockedExternalUrlError(url);
        downloadUrl(url);
        zone.clearSource(downloadSource);
      } catch {
        zone?.push('The download could not be started. Please try again.', 'error', { source: downloadSource });
      } finally {
        setDownloading(false);
      }
    } else if (entry.action === 'navigate' && entry.actionUrl?.startsWith('/')) {
      // Deep-link to the relevant app page and close the dropdown behind us.
      closePanel();
      void navigate(entry.actionUrl);
    } else if (entry.action === 'detail') {
      onToggleExpanded();
    }
  }, [entry, isPending, onMarkRead, onToggleExpanded, zone, downloadSource, navigate, closePanel]);

  const actionLabel = isPending
    ? null
    : entry.action === 'download'
      ? 'Download'
      : canNavigate
        ? 'View'
        : entry.action === 'detail'
          ? 'View details'
          : null;

  return (
    <>
      <div
        className={clsx(row, isClickable && rowClickable, unread && rowUnread)}
        data-testid={`stream-entry-${entry.id}`}
        data-subject={entry.subject ?? undefined}
        onClick={handleClick}
        style={{ opacity: isPending ? 0.75 : 1 }}
      >
        <div className={leadCol}>{unread && <span className={unreadDot} />}</div>
        <div
          style={{ marginTop: 2, lineHeight: 0 }}
          data-testid={`stream-entry-${entry.id}-icon`}
          data-icon={downloading || isPending ? 'spinner' : resolvedIconName(entry.icon)}
        >
          {downloading || isPending ? <Spinner size={18} /> : ICON_MAP[resolvedIconName(entry.icon)]}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text
            as="span"
            style={{
              display: 'block',
              fontSize: vars.font.size.base,
              fontWeight: unread ? 500 : 400,
              lineHeight: 1.4,
              wordBreak: 'break-word',
              color: vars.color.neutral.dark,
            }}
          >
            {entry.message}
          </Text>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 3 }}>
            <Text as="span" variant="caption" color="secondary">
              {formatTimestamp(entry.created)}
            </Text>
            {isPending && (
              <Text as="span" variant="caption" color="secondary">
                · In progress
              </Text>
            )}
            {actionLabel && (
              <button
                type="button"
                className={linkButton}
                // #875c: the staging smoke clicks a row's own action (View /
                // Download / View details) by id, never by role + name.
                data-testid={`stream-entry-${entry.id}-action`}
                onClick={(e: React.MouseEvent<Element>) => {
                  e.stopPropagation();
                  void handleClick();
                }}
              >
                {actionLabel}
              </button>
            )}
          </span>
        </div>
      </div>
      {!isPending && entry.action === 'detail' && expanded && <EntryDetail entry={entry} />}
    </>
  );
}
